import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import composeRefs from '@seznam/compose-react-refs';

import { tTitle } from "i18n";
import { FormGroup, FormGroupStyle, Wrapper } from "ui/control";
import { useUndoableField } from "ui/control/field/undoable-field";
import FieldError from "ui/control/field/FieldError";
import { Icon, msVisibility, msVisibilityOff } from "ui/material-symbols";
import "./InputField.css";

interface Props {
    name: string;
    title?: string;
    placeholder?: string;
    tooltip?: string;
    type?: "text" | "password" | "email" | "url";
    disabled?: boolean;
    maxLength?: number;
    horizontal?: boolean;
    layout?: FormGroupStyle;
    groupClassName?: string;
    labelClassName?: string;
    inputClassName?: string;
    col?: string;
    autoFocus?: boolean;
    showOk?: boolean;
    showWrong?: boolean;
    showError?: boolean;
    error?: string | null;
    note?: string | null;
    className?: string;
    autoComplete?: string;
    initialValue?: string | null;
    defaultValue?: string | null;
    ref?: React.Ref<HTMLInputElement>;
}

export function InputField(
    {
        name, title, placeholder, tooltip, type = "text", disabled, maxLength, horizontal = false, layout,
        groupClassName, labelClassName, inputClassName, col, autoFocus, showOk = false, showWrong = false,
        showError = false, error: externalError, note, className, autoComplete, initialValue, defaultValue, ref
    }: Props
) {
    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const {t} = useTranslation();

    useEffect(() => {
        if (autoFocus && inputRef.current != null) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    const [inputProps, {touched, error: fieldError}, , {undo, reset, onUndo, onReset}] =
        useUndoableField<string>(name, initialValue, defaultValue);

    const error = externalError ?? (touched ? fieldError : undefined);

    return (
        <FormGroup
            title={title}
            name={name}
            horizontal={horizontal}
            layout={layout}
            labelClassName={labelClassName}
            groupClassName={groupClassName}
            tooltip={tooltip}
            undo={undo}
            reset={reset}
            onUndo={onUndo}
            onReset={onReset}
        >
            <Wrapper className={col}>
                <Wrapper className={type === "password" ? "password-field" : undefined}>
                    <input
                        {...inputProps}
                        id={name}
                        type={type === "password" && showPassword ? "text" : type}
                        className={cx(
                            "form-control", {
                                "is-valid": showOk && !error,
                                "is-invalid": showWrong && error,
                                [className!]: !!className
                            },
                            inputClassName
                        )}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        disabled={disabled}
                        maxLength={maxLength}
                        ref={composeRefs(ref, inputRef)}
                    />
                    {showError && error ?
                        <FieldError error={error}/>
                    :
                        note && <small className="form-text">{note}</small>
                    }
                    {type === "password" && ((!error && !showOk) || (error && !showWrong)) &&
                        <button
                            className="show-password"
                            type="button"
                            title={tTitle(showPassword ? t("hide-password") : t("show-password"))}
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            <Icon icon={showPassword ? msVisibility : msVisibilityOff} size="2em"/>
                        </button>
                    }
                </Wrapper>
            </Wrapper>
        </FormGroup>
    );
}
